<script setup>
import { CheckCircleIcon } from '@heroicons/vue/24/outline'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import ClickToActionSection from '@/components/ClickToActionSection.vue'

defineProps({
    projects: {
        type: Array,
        required: true,
    },
})

const stats = [
    { label: 'let zkušeností', value: '30+' },
    { label: 'spokojených klientů', value: '500+' },
    { label: 'dokončených projektů', value: '1000+' },
]

const features = [
    'Osvědčení pro práci s plynem',
    'Certifikát EKOPLASTIK',
    'Oprávnění TECE',
    'Autorizovaný servis Vaillant',
]
</script>

<template>
    <Head title="O nás">
        <meta
            name="description"
            content="Poznejte rodinnou firmu Kalianko - od roku 1995 poskytujeme profesionální služby v oblasti vodoinstalací, topenářství a plynoinstalací. 500+ spokojených klientů a 1000+ dokončených projektů."
        />
    </Head>

    <!-- Hero Section -->
    <section class="relative isolate overflow-hidden bg-gray-900 py-24 sm:py-32">
        <div class="from-brand-primary/20 absolute inset-0 -z-10 bg-linear-to-br via-gray-900 to-gray-900" />
        <div
            class="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-gray-900/80 shadow-xl ring-1 ring-white/10 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center"
        />

        <div class="section-width relative">
            <div class="mx-auto max-w-2xl text-center">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">O nás</h1>
                <p class="mt-6 text-lg/8 text-gray-300">Jsme rodinná firma s tradicí a osobním přístupem</p>
                <p class="mt-6 text-lg/8 text-gray-300">
                    Na trhu působíme od roku 1995. Dbáme na dobrou pověst firmy a odvádíme svou práci tak, aby byli
                    spokojeni jak naši zákazníci, tak my. Služby poskytujeme včetně profesionálního poradenství ohledně
                    technického řešení, výběru, nákupu technologie a zařizovacích předmětů. Od roku 2011 se
                    specializujeme na maloobchodní prodej instalaterskeho materiálu.
                </p>
            </div>
        </div>
    </section>

    <!-- CTA Section -->
    <section class="px-2 lg:px-0">
        <div class="section-width py-16 sm:py-24">
            <ClickToActionSection />
        </div>
    </section>

    <!-- About Content -->
    <section class="px-2 lg:px-0">
        <div class="section-width py-16 sm:py-24">
            <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <div>
                    <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše firma</p>
                    <h2 class="heading-2 mt-2">Spolehlivý partner pro vaše instalace</h2>
                    <p class="paragraph mt-4">
                        Specializujeme se na kompletní instalace v rodinných domech, bytech i komerčních prostorech. Ať
                        už potřebujete novou koupelnu, výměnu kotle, nebo kompletní rekonstrukci rozvodů - jsme tu pro
                        vás.
                    </p>
                    <p class="paragraph mt-4">
                        Pracujeme s kvalitními materiály od renomovaných výrobců a na naši práci poskytujeme záruku.
                        Vaše bezpečnost a spokojenost je pro nás prioritou.
                    </p>

                    <!-- Certifications as simple list -->
                    <div class="mt-8">
                        <h3 class="text-sm font-semibold text-gray-900">Naše certifikace</h3>
                        <ul class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <li
                                v-for="feature in features"
                                :key="feature"
                                class="flex items-center gap-2 text-sm text-gray-600"
                            >
                                <CheckCircleIcon class="text-brand-primary size-5 shrink-0" />
                                {{ feature }}
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="relative aspect-square overflow-hidden rounded-2xl lg:aspect-4/3">
                    <img
                        src="/images/o-nas-firma.webp"
                        alt="Tým Kalianko při práci"
                        class="size-full object-cover"
                    />
                </div>
            </div>
        </div>
    </section>

    <!-- Stats Section -->
    <section class="bg-gray-900 px-2 py-16 sm:py-24 lg:px-0">
        <div class="section-width">
            <div class="mx-auto max-w-4xl">
                <div class="grid grid-cols-3 gap-8 text-center">
                    <div
                        v-for="stat in stats"
                        :key="stat.label"
                    >
                        <p class="text-brand-primary text-4xl font-bold tracking-tight sm:text-5xl">{{ stat.value }}</p>
                        <p class="paragraph mt-4 text-gray-300">{{ stat.label }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Projects Section -->
    <section class="section-width px-2 py-16 sm:py-24 lg:px-0">
        <div class="mx-auto max-w-2xl text-center">
            <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše práce</p>
            <h2 class="heading-2 mt-2">Nejnovější projekty</h2>
            <p class="paragraph mt-4">Podívejte se na některé z našich nedávných realizací pro spokojené zákazníky.</p>
        </div>

        <div class="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <ProjectCard
                v-for="project in projects"
                :key="project.id"
                :project="project"
            />
        </div>
    </section>
</template>
